import React, { useState, useEffect } from 'react';

import {
    Tooltip,
    InputLabel,
    IconButton,
    MenuItem,
    FormControl,
    Select,
    Autocomplete,
    TextField
} from '@mui/material';
import ClearIcon from "@mui/icons-material/Clear";
import Swal from 'sweetalert2';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';
import { useDebounce } from "use-debounce";

import AppLayout from '../../components/Layouts/AppLayout';
import AddButton from '../../components/AddButton';
import axios from '../../lib/axios';
import { useStateContext } from '../../context/ContextProvider';
import { useAuth } from '../../hooks/auth';
import Loading from '../../components/Loading'
import ServerTable from '../../components/ServerTable';
import { handleAxiosError } from '../../helpers/helper';
import CreateServiceProvider from './create/CreateServiceProvider';
import serviceProviderAPI from "../../services/api/ServiceProviders/serviceProviderAPI";
import customerAPI from '../../services/api/Customers/customersAPI';
import vendorAPI from '../../services/api/Vendors/vendorAPI';
import { useLocalStorage } from "@uidotdev/usehooks";

const ServiceProviders = () => {
    const { t } = useTranslation()
    const { companies, company_id, pusher, config } = useStateContext()
    const { user } = useAuth({ middleware: 'guest' })

    const [isLoading, setIsLoading] = useState(false)
    const [openCreateServiceProviderModal, setOpenCreateServiceProviderModal] = useState(false);
    const [serviceProviders, setServiceProviders] = useState([])
    const [customers, setCustomers] = useState([])
    const [vendors, setVendors] = useState([])

    const [filters, setFilters] = useLocalStorage('filters');
    const [search, setSearch] = useState(() => {
        const defaultSearch = {
            name: '',
            company: '',
            customer: null,
            vendor: null
        }

        if(filters) {
            try {
                return {
                    name: filters[0]?.service_provider_name,
                    company: filters[0]?.service_provider_company,
                    customer: null,
                    vendor: null
                };
            } catch (e) {
                return defaultSearch;
            }
        }

        return defaultSearch;
    })
    const [customerInputValue, setCustomerInputValue] = useState('')
    const [vendorInputValue, setVendorInputValue] = useState('')

    const [debouncedSearchName] = useDebounce(search.name, 500)

    const [random, setRandom] = useState(null)

    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)

    const [model, setModel] = useState({
        actions: true,
        id: true,
        name: true,
        customer: true,
        vendor: true,
        company: false
    })

    useEffect(() => {
        getTableView()
    }, [])

    const getTableView = async () => {
        setIsLoading(true)
        await axios.get(`/api/get-table-view?view_name=service_providers_table&user_id=${user?.id}`, config)
            .then(res => {
                const model = res.data
                if (Object.keys(model?.success?.data).length > 0) {
                    setModel(model.success.data)
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    useEffect(() => {
        const channelServiceProviderDelete = pusher.subscribe(`${localStorage.getItem('client_id')}-serviceprovider-deleted`)
        const channelServiceProviderCreate = pusher.subscribe(`${localStorage.getItem('client_id')}-serviceprovider-created`)
        const channelServiceProviderUpdate = pusher.subscribe(`${localStorage.getItem('client_id')}-serviceprovider-updated`)

        channelServiceProviderDelete.bind(`${localStorage.getItem('client_id')}-serviceprovider-deleted-event`, data => {
            setRandom(Math.random())
        })

        channelServiceProviderCreate.bind(`${localStorage.getItem('client_id')}-serviceprovider-created-event`, data => {
            setRandom(Math.random())
        })

        channelServiceProviderUpdate.bind(`${localStorage.getItem('client_id')}-serviceprovider-updated-event`, data => {
            setRandom(Math.random())
        })

        return (() => {
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-serviceprovider-deleted`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-serviceprovider-created`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-serviceprovider-updated`)
        })
    }, [])

    useEffect(() => {
        getCustomersAndVendors()
    }, [])

    const getCustomersAndVendors = async () => {
        setIsLoading(true)

        await customerAPI.getCustomers(
            config,
            {
                where: {
                    company_id: company_id
                }
            }
        )
            .then((data) => {
                setCustomers(data)

                return vendorAPI.getVendors(
                    config,
                    {
                        where: {
                            company_id: company_id
                        }
                    }
                )
            })
            .then((data) => setVendors(data))
            .catch(({ response }) => console.log(response))
            .finally(() => setIsLoading(false))
    }

    const getServiceProviders = async (name, company, customer, vendor, page, pageSize) => {
        setIsLoading(true)

        await serviceProviderAPI.getServiceProviders(
            config,
            {
                where: {
                    name: name,
                    company_id: company,
                    customer_id: customer,
                    vendor_id: vendor,
                    page: page + 1,
                    page_size: pageSize
                },
                with: [
                    'company',
                    'customer',
                    'vendor'
                ]
            }
        )
            .then((data) => {
                setServiceProviders(data?.data ?? [])
                setTotal(data?.meta?.total ?? 0)
            })
            .finally(() => setIsLoading(false))
    }

    useEffect(() => {
        getServiceProviders(
            debouncedSearchName,
            search.company,
            search.customer?.id,
            search.vendor?.id,
            page,
            pageSize
        )
    }, [debouncedSearchName, search.company, search.customer, search.vendor, page, pageSize, random])


    /* methods */
    const handleOpen = () => setOpenCreateServiceProviderModal(true);
    const handleClose = () => setOpenCreateServiceProviderModal(false);

    const handleChangeSearch = (event, key, value = null) => {
        switch(key) {
            case "customer":
            case "vendor":
                setSearch((prev) => ({
                    ...prev,
                    [key]: value
                }));
                break;
            default:
                setSearch((prev) => ({
                    ...prev,
                    [key]: event.target.value
                }));
        }
    }

    const handleClearSearch = (event, key) => {
        setSearch((prev) => ({
            ...prev,
            [key]: ''
        }));
    }

    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            width: 70
        },
        {
            field: 'name',
            headerName: t('name'),
            flex: 1,
            minWidth: 210
        },
        {
            field: 'customer',
            headerName: t('customer'),
            flex: 1,
            minWidth: 210,
            valueGetter: (value, row) => `${value?.value?.name ?? ''}`
        },
        {
            field: 'vendor',
            headerName: t('vendor'),
            flex: 1,
            minWidth: 210,
            valueGetter: (value, row) => `${value?.value?.name ?? ''}`
        },
        {
            field: 'company',
            headerName: t('company'),
            flex: 1,
            minWidth: 210,
            valueGetter: (value, row) => `${value?.value?.name || ''}`,
        },
        {
            field: 'actions',
            headerName: t('actions'),
            sortable: false,
            cellClassName: 'padding-0',
            renderCell: (params) => <RenderDetailsButton params={params} setIsLoading={setIsLoading} />,
            width: 140
        },
    ];

    const createFilterArray = (search) => {
        if(filters) {
            let filter = [{}]
            setFilters(JSON.stringify(filter))
        }

        filters[0].service_provider_name = search.name
        filters[0].service_provider_company = search.company

        setFilters(filters)
    }

    useEffect(() => {
        createFilterArray(search)
    }, [search])

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <AppLayout showFooter={!openCreateServiceProviderModal}>
                <div className='p-5 pt-0'>
                    <div className='pb-5 bg-white mb-2 rounded-md'>
                        <div className='flex justify-between p-5 items-center border-b'>
                            <p style={{ fontWeight: 600, fontSize: '16px' }}>{t('x_list', {x: t('service_provider')})}</p>
                            <Tooltip disableInteractive title={t('create_new_x', {x: t('service_provider')})} placement='bottom'>
                                <div>
                                    <AddButton onClick={handleOpen}><i className="fa-solid fa-plus"></i></AddButton>
                                </div>
                            </Tooltip>
                        </div>
                        <div className='flex flex-wrap justify-between items-end w-full border-b pb-5'>
                            <div className='px-5 pt-5 w-full md:w-1/2'>
                                <div className='flex justify-between items-center search'>
                                    <input type="text" placeholder={t('search_by_name')} value={search.name} className='w-full bg-[#f5f5f5] border-0 focus:ring-0 p-0 font-bold place' onChange={(e) => handleChangeSearch(e, 'name')} />
                                    <i className="fa-solid fa-magnifying-glass" style={{ color: 'rgba(0,0,0,.54)' }}></i>
                                </div>
                            </div>

                            <div className='px-5 pt-5 w-full md:w-1/2'>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel id="demo-simple-select-standard-label">{t('search_by_x', {x: t('company')})}</InputLabel>
                                    <Select
                                        value={search.company}
                                        onChange={(e) => handleChangeSearch(e, 'company')}
                                        label="Search company"
                                        sx={{
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: 'none !important' }, background: '#F5F5F5', borderRadius: '6px',
                                            ".MuiSelect-iconOutlined": { display: search.company ? 'none !important' : '' }, "&.Mui-focused .MuiIconButton-root": { color: 'rgba(0,0,0,.42)' }
                                        }}
                                        endAdornment={search.company ? (<IconButton sx={{ visibility: search.company ? "visible" : "hidden", padding: '0' }} onClick={(e) => handleClearSearch(e, 'company')}><ClearIcon /></IconButton>) : false}
                                    >
                                        {
                                            companies.map((item, index) => <MenuItem key={index} value={item.id}>{item.name}</MenuItem>)
                                        }
                                    </Select>
                                </FormControl>
                            </div>

                            <div className='px-5 pt-5 w-full md:w-1/2'>
                                <FormControl sx={{ width: '100%' }}>
                                    <Autocomplete
                                        disablePortal
                                        id="customer-autocomplete"
                                        options={customers?.map(option => ({ label: option.name, id: option.id }))}
                                        getOptionLabel={(option) => option?.label ?? ''}
                                        renderOption={(props, option) => (
                                            <li {...props} key={`vendor-${option.id}`}>
                                                {option.label}
                                            </li>
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        sx={{
                                            boxShadow: 'none',
                                            '.MuiOutlinedInput-notchedOutline': {
                                                border: 'none !important'
                                            },
                                            background: '#F5F5F5',
                                            borderRadius: '6px',
                                            "&.Mui-focused .MuiIconButton-root": {
                                                color: 'rgba(0,0,0,.42)'
                                            }
                                        }}
                                        value={search.customer}
                                        onChange={(a, b, reason) => handleChangeSearch(a, 'customer', b)}
                                        componentsProps={{
                                            clearIndicator: {
                                                onClick: (e) => handleChangeSearch(e, 'customer', null)
                                            }
                                        }}
                                        renderInput={(params) => <TextField {...params} autoFocus={true} label={t('search_by_x', {x: t('customer')})} />}
                                    />
                                </FormControl>
                            </div>

                            <div className='px-5 pt-5 w-full md:w-1/2'>
                                <FormControl sx={{ width: '100%' }}>
                                    <Autocomplete
                                        disablePortal
                                        id="vendor-autocomplete"
                                        options={vendors?.map(option => ({ label: option.name, id: option.id }))}
                                        getOptionLabel={(option) => option?.label ?? ''}
                                        renderOption={(props, option) => (
                                            <li {...props} key={`vendor-${option.id}`}>
                                                {option.label}
                                            </li>
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        sx={{
                                            boxShadow: 'none',
                                            '.MuiOutlinedInput-notchedOutline': {
                                                border: 'none !important'
                                            },
                                            background: '#F5F5F5',
                                            borderRadius: '6px',
                                            "&.Mui-focused .MuiIconButton-root": {
                                                color: 'rgba(0,0,0,.42)'
                                            }
                                        }}
                                        value={search.vendor}
                                        onChange={(a, b, reason) => handleChangeSearch(a, 'vendor', b)}
                                        componentsProps={{
                                            clearIndicator: {
                                                onClick: (e) => handleChangeSearch(e, 'vendor', null)
                                            }
                                        }}
                                        renderInput={(params) => <TextField {...params} autoFocus={true} label={t('search_by_x', {x: t('vendor')})} />}
                                    />
                                </FormControl>
                            </div>
                        </div>
                        <div className='pt-3 w-full'>
                            <ServerTable
                                columns={columns}
                                items={serviceProviders}
                                total={total}
                                setPage={setPage}
                                setPageSize={setPageSize}
                                page={page}
                                pageSize={pageSize}
                                columnVisibilityModel={model}
                                setModel={setModel}
                                view='service_providers'
                            />
                        </div>
                    </div>
                </div>
            </AppLayout>
            <CreateServiceProvider
                open={openCreateServiceProviderModal}
                handleClose={handleClose}
                setIsLoading={setIsLoading}
                config={config}
                companyId={company_id}
                customers={customers}
                vendors={vendors}
            />
        </>
    )

}

export default ServiceProviders


const RenderDetailsButton = ({ params }) => {
    const { t } = useTranslation()
    const { config } = useStateContext()

    const deleteServiceProvider = async (id) => {
        const isConfirm = await Swal.fire({
            title: `${t('title_delete--long', {x: t('service_provider').toLocaleLowerCase(), y: params.row.name})}`,
            text: t('text_delete'),
            icon: 'warning',
            customClass: 'error',
            showCloseButton: true,
            iconColor: '#FF0000',
            reverseButtons: true,
            showCancelButton: true,

            confirmButtonText: t('confirmButtonText_delete'),
            cancelButtonText: t('cancel')
        }).then((result) => {
            return result.isConfirmed
        })

        if (!isConfirm) {
            return
        }
        await axios
            .delete(`/api/service-providers/${id}`, config)
            .then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data.success.message
            })
            })
            .catch(({ response }) => {
                handleAxiosError({response: response})
            })
    }

    return (
        <>
            <div className='flex justify-between'>
                <Tooltip disableInteractive title={t('edit')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <Link to={`/service-providers/${params.row.id}`}>
                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200"><i className="fa-solid fa-pencil"></i></span>
                        </Link>
                    </div>
                </Tooltip>
                <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                    <div style={{ color: 'rgba(0,0,0,.54)' }}>
                        <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => deleteServiceProvider(params.row.id)}><i className="fa-solid fa-trash"></i></span>
                    </div>
                </Tooltip>
            </div>
        </>
    )
}
